import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Target, Users, Shield, Zap } from 'lucide-react';

const AboutUs = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold tracking-tight">SAANJH</h1>
          <Button variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Empowering Small Businesses to Thrive Online
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            SAANJH is a revolutionary QR-powered e-commerce platform designed to help small businesses, 
            food vendors, restaurants, and boutiques create their online presence in minutes.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                  <Target className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">Our Mission</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    To democratize e-commerce by providing an affordable, easy-to-use platform that enables 
                    every small business owner to establish their digital storefront and reach customers 
                    through QR code technology.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-primary/10 rounded-lg flex-shrink-0">
                  <Zap className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold mb-3">Our Vision</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    To become India's leading QR-based e-commerce platform, connecting thousands of local 
                    businesses with their communities and helping them grow in the digital economy.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Story Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold tracking-tight mb-6 text-center">Our Story</h2>
          <div className="max-w-4xl mx-auto space-y-4 text-muted-foreground leading-relaxed">
            <p>
              SAANJH was born from a simple observation: while large businesses have the resources to build 
              sophisticated online stores, small vendors struggle with expensive solutions and complex 
              technology. We saw street food vendors, boutique owners, and local artisans missing out on 
              the digital revolution.
            </p>
            <p>
              We asked ourselves: "What if we could make it as easy as printing a QR code?" That's when 
              SAANJH came to life. We built a platform where any business owner can sign up, add their 
              products, and get a QR code that customers can scan to browse and order.
            </p>
            <p>
              Located in the heart of Ludhiana, Punjab, we understand the challenges faced by local 
              businesses. Our platform is designed with Indian vendors in mind - supporting Indian 
              payment methods through Razorpay, offering regional language support, and keeping pricing 
              affordable for all.
            </p>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center">Why Choose SAANJH?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 text-blue-600 mb-4">
                  <Zap className="h-6 w-6" />
                </div>
                <h3 className="font-semibold mb-2">Quick Setup</h3>
                <p className="text-sm text-muted-foreground">
                  Get your online store running in under 10 minutes
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-green-100 text-green-600 mb-4">
                  <Shield className="h-6 w-6" />
                </div>
                <h3 className="font-semibold mb-2">Secure Payments</h3>
                <p className="text-sm text-muted-foreground">
                  RBI-approved Razorpay integration for safe transactions
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-purple-100 text-purple-600 mb-4">
                  <Users className="h-6 w-6" />
                </div>
                <h3 className="font-semibold mb-2">Customer Friendly</h3>
                <p className="text-sm text-muted-foreground">
                  Simple scan-and-shop experience for your customers
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-orange-100 text-orange-600 mb-4">
                  ₹
                </div>
                <h3 className="font-semibold mb-2">Affordable</h3>
                <p className="text-sm text-muted-foreground">
                  Plans starting at just ₹123/month
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Features Overview */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center">What We Offer</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">QR Code Storefront</h3>
                <p className="text-sm text-muted-foreground">
                  Get a unique QR code that customers can scan to access your store instantly
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Easy Product Management</h3>
                <p className="text-sm text-muted-foreground">
                  Add products manually or bulk upload via CSV files
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Customizable Themes</h3>
                <p className="text-sm text-muted-foreground">
                  Personalize your store with custom colors, fonts, and branding
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Secure Payments</h3>
                <p className="text-sm text-muted-foreground">
                  Accept UPI, cards, net banking, and wallets through Razorpay
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Order Management</h3>
                <p className="text-sm text-muted-foreground">
                  Track all orders and customer information in one dashboard
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="text-primary text-2xl">✓</div>
              <div>
                <h3 className="font-semibold mb-1">Custom Pages</h3>
                <p className="text-sm text-muted-foreground">
                  Create about us, policies, and other pages with our WYSIWYG editor
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Trust & Security */}
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 mb-16">
          <CardContent className="pt-8 pb-8">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg mb-6">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold mb-4">Your Trust, Our Priority</h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                We understand that security is paramount when it comes to online payments. That's why we've 
                partnered with Razorpay, India's leading payment gateway that is RBI-approved and PCI DSS Level 1 
                certified. We never store your payment information on our servers - all transactions are 
                processed through Razorpay's bank-grade security infrastructure.
              </p>
              <div className="flex flex-wrap justify-center gap-4 text-sm">
                <div className="bg-white px-4 py-2 rounded-full font-medium">
                  🔒 256-bit SSL Encryption
                </div>
                <div className="bg-white px-4 py-2 rounded-full font-medium">
                  ✓ RBI Approved
                </div>
                <div className="bg-white px-4 py-2 rounded-full font-medium">
                  ✓ PCI DSS Certified
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Section */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold tracking-tight mb-4">Get In Touch</h2>
          <p className="text-muted-foreground mb-8">
            Have questions or want to learn more? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={() => navigate('/contact')}>
              Contact Us
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/vendor/auth')}>
              Start Your Store
            </Button>
          </div>
        </div>

        {/* Company Details */}
        <Card className="max-w-3xl mx-auto">
          <CardContent className="pt-6">
            <div className="text-center text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-2">SAANJH</p>
              <p>Head Office, Sant Nagar, Civil Lines</p>
              <p>Ludhiana - 141001, Punjab, India</p>
              <p className="mt-3">
                Email: <a href="mailto:admin@saanjh.cc" className="text-primary hover:underline">admin@saanjh.cc</a>
              </p>
              <p>
                Phone: <a href="tel:+917837640500" className="text-primary hover:underline">+91 78376 40500</a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <footer className="border-t py-8 mt-16">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SAANJH. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AboutUs;